/// <amd-module name="Build/Extensions/GitHubEnterprise/GitHubEnterpriseWizard" />
import * as React from "react";

import * as Build from "Build/Client/Contracts/Build";

import { StepCompletedCallback, WizardState } from "Build/Designer/Wizard.types";

import { ConnectionSelection } from "Build/Extensions/GitHubEnterprise/ConnectionSelection";
import { RepositorySelection } from "Build/Extensions/GitHubEnterprise/RepositorySelection";
import * as Resources from "Build/Extensions/GitHubEnterprise/Resources";

import { IVssHistoryService, StateMergeOptions } from "VSS/Platform/History";
import { Components, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";

import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";

export enum Steps {
    SelectConnection = 1,
    SelectRepository = 2
}

export interface Props {
    wizardState: WizardState;
    onComplete: StepCompletedCallback;
    onUpdate: (action: string, properties?: { [key: string]: any }) => void;
    onTTI: () => void;
}

export interface State {
    stepNumber: number;
    connectionId?: string;
    repositoryId?: string;
    errorMessage?: string;
}

namespace WizardStateKeys {
    export const ConnectionId = "connectionId";
    export const RepositoryId = "repositoryId";
}

export class GitHubEnterpriseReposWizard extends VssComponent<Props, State, IVssComponentContext> {
    constructor(props: Props, context: IVssComponentContext) {
        super(props, context);

        this.state = this._getState(props);
    }

    public render(): JSX.Element {
        return (<>
        {
            this.state.errorMessage && <MessageCard className={"github-enterprise-wizard-error"} severity={MessageCardSeverity.Error}>
                {this.state.errorMessage}
            </MessageCard>
        }
        {
            this._renderStep()
        }
        </>);
    }

    public componentWillReceiveProps(newProps: Props) {
        this.setState(this._getState(newProps));
    }

    public componentDidUpdate(prevProps: Props, prevState: State) {
        // When moving back from selecting a repository to selecting a connection, remove any error messages
        if (prevState && prevState.stepNumber == Steps.SelectRepository && this.state.stepNumber == Steps.SelectConnection) {
            this._clearErrorMessage();
        }
    }

    private _renderStep(): JSX.Element {
        switch (this.state.stepNumber) {
            default:
            case Steps.SelectConnection:
                return <ConnectionSelection onSelectConnection={this._onSelectConnection}
                                            serviceName={Resources.GitHubEnterprise}
                                            sourceProvider={this.props.wizardState.sourceProvider}
                                            onErrorMessage={this._onErrorMessage}/>;
            case Steps.SelectRepository:
                return <RepositorySelection onSelectRepository={this._onSelectRepository}
                                            connectionId={this.state.connectionId!}
                                            serviceName={Resources.GitHubEnterprise}
                                            sourceProvider={this.props.wizardState.sourceProvider}
                                            onRepositoryListUpdate={this._onRepositoryListUpdate}
                                            onErrorMessage={this._onErrorMessage}
                                            onTTI={this.props.onTTI}/>;
        }
    }

    private _onSelectConnection = (connectionId: string): void => {
        const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");

        const currentState = historyService.getState();
        const url: string = historyService.generateUrl({
            ...currentState,
            connectionId: connectionId
        }, StateMergeOptions.none);

        historyService.pushState(undefined, url);

        this.setState({
            stepNumber: Steps.SelectRepository,
            connectionId: connectionId
        });
    }

    private _onSelectRepository = (repository: Build.SourceRepository, properties: { [key: string]: string }): void => {
        this.props.onComplete({
            repositoryId: repository.id,
            repositoryName: repository.id,
            branch: repository.defaultBranch,
            connectionId: this.state.connectionId!,
            repositoryUrl: properties.repositoryUrl
        }, properties);
    }

    private _getState(props: Props): State {
        const connectionId: string = props.wizardState[WizardStateKeys.ConnectionId];
        const repositoryId: string = props.wizardState[WizardStateKeys.RepositoryId];
        const stepNumber: number = (connectionId && !repositoryId) ? Steps.SelectRepository : Steps.SelectConnection;

        return {
            stepNumber: stepNumber,
            connectionId: connectionId,
            repositoryId: repositoryId
        };
    }

    private _onErrorMessage = (error: string): void => {
        this.setState({ errorMessage: error });
    }

    private _clearErrorMessage = () => {
        this.setState({
            errorMessage: undefined
        });
    }

    private _onRepositoryListUpdate = (action: string, properties?: { [key: string]: any }): void => {
        this.props.onUpdate(action, properties);
    }
}

Components.add("github-enterprise-wizard", GitHubEnterpriseReposWizard);
